import React, { useEffect, useState, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import { Copy, Send, Activity, Loader2 } from 'lucide-react';
import { logger } from '../lib/logger';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const statBadge = (n) => (
  <span className="font-mono text-[18px] font-bold text-[#0B1E3F]">{n}</span>
);

export default function AdminNurture() {
  const { toast } = useToast();
  const [stats, setStats] = useState(null);
  const [log, setLog] = useState([]);
  const [config, setConfig] = useState(null);
  const [configDraft, setConfigDraft] = useState(null);
  const [bootcampCfg, setBootcampCfg] = useState(null);
  const [bootcampDraft, setBootcampDraft] = useState(null);
  const [bootcampSignups, setBootcampSignups] = useState(null);
  const [testEmail, setTestEmail] = useState('');
  const [testName, setTestName] = useState('');
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  // PDF Tracks tab state
  const [byTrack, setByTrack] = useState(null);              // { tracks: [...] }
  const [selectedTrack, setSelectedTrack] = useState('parents_pdf');
  const [trackLog, setTrackLog] = useState([]);
  const [trackLogLoading, setTrackLogLoading] = useState(false);
  const [pdfTestEmail, setPdfTestEmail] = useState('');
  const [pdfTestName, setPdfTestName] = useState('Coach');
  const [pdfTestTrack, setPdfTestTrack] = useState('all');   // 'all' or a track key
  const [pdfTestBusy, setPdfTestBusy] = useState(false);
  const [pdfTestResult, setPdfTestResult] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, l, c, bc, bs, bt] = await Promise.all([
        fetch(`${BACKEND}/api/admin/nurture/stats`, { credentials: 'include' }).then((r) => r.json()),
        fetch(`${BACKEND}/api/admin/nurture/recent-log?limit=50`, { credentials: 'include' }).then((r) => r.json()),
        fetch(`${BACKEND}/api/admin/nurture/config`, { credentials: 'include' }).then((r) => r.json()),
        fetch(`${BACKEND}/api/admin/bootcamp/config`, { credentials: 'include' }).then((r) => r.json()),
        fetch(`${BACKEND}/api/admin/bootcamp/signups?limit=100`, { credentials: 'include' }).then((r) => r.json()),
        fetch(`${BACKEND}/api/admin/nurture/by-track-stats`, { credentials: 'include' }).then((r) => r.json()),
      ]);
      setStats(s);
      setLog(l.entries || []);
      setConfig(c);
      setConfigDraft(c);
      setBootcampCfg(bc);
      setBootcampDraft(bc);
      setBootcampSignups(bs);
      setByTrack(bt);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Fetch per-track recent log whenever the selected track changes.
  const fetchTrackLog = useCallback(async (track) => {
    setTrackLogLoading(true);
    try {
      const r = await fetch(
        `${BACKEND}/api/admin/nurture/log-by-track?track=${encodeURIComponent(track)}&limit=50`,
        { credentials: 'include' }
      ).then((rr) => rr.json());
      setTrackLog(r.entries || []);
    } catch {
      setTrackLog([]);
    } finally {
      setTrackLogLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedTrack) fetchTrackLog(selectedTrack);
  }, [selectedTrack, fetchTrackLog]);

  const sendPdfTestSequence = async (e) => {
    e.preventDefault();
    const target = pdfTestEmail.trim().toLowerCase();
    if (!target) return;
    setPdfTestBusy(true);
    setPdfTestResult(null);
    try {
      const body = {
        email: target,
        first_name: pdfTestName.trim() || 'Test',
        track: pdfTestTrack === 'all' ? null : pdfTestTrack,
      };
      const r = await fetch(`${BACKEND}/api/admin/nurture/send-test-sequence`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await r.json();
      if (r.ok) {
        setPdfTestResult(data);
        toast({
          title: `Test sequence dispatched`,
          description: `Sent ${data.sent_count} · Failed ${data.failed_count} → ${target}`,
        });
      } else {
        toast({
          title: 'Send failed',
          description: data.detail || 'Unknown error',
          variant: 'destructive',
        });
      }
    } finally { setPdfTestBusy(false); }
  };

  const triggerNow = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${BACKEND}/api/admin/nurture/trigger-now`, {
        method: 'POST', credentials: 'include',
      }).then((rr) => rr.json());
      toast({
        title: 'Worker triggered',
        description: `Processed ${r.processed} due contact(s).`,
      });
      await fetchAll();
    } finally { setBusy(false); }
  };

  const addTestContact = async (e) => {
    e.preventDefault();
    if (!testEmail.trim()) return;
    setBusy(true);
    try {
      const r = await fetch(`${BACKEND}/api/admin/nurture/add-test-contact`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: testEmail.trim(), first_name: testName.trim() || 'Test' }),
      });
      if (r.ok) {
        const body = await r.json();
        toast({
          title: 'Test contact added',
          description: `${testEmail} enrolled. Email 1 fires on the next worker tick (≤60s) or click "Send now".`,
        });
        try {
          await fetch(`${BACKEND}/api/admin/nurture/send-now/${body.contact_id}`, {
            method: 'POST', credentials: 'include',
          });
          toast({ title: 'Email 1 sent', description: `→ ${testEmail}` });
        } catch (sendErr) {
          // Non-fatal — the contact was created successfully above; only
          // the inline "send now" follow-up failed. Surface in devtools.
          logger.warn('[AdminNurture] inline send-now failed', sendErr);
        }
        setTestEmail(''); setTestName('');
        await fetchAll();
      } else {
        const body = await r.json().catch(() => ({}));
        toast({ title: 'Failed to add', description: body.detail || 'Unknown error', variant: 'destructive' });
      }
    } finally { setBusy(false); }
  };

  const saveConfig = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${BACKEND}/api/admin/nurture/config`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(configDraft),
      });
      if (r.ok) {
        const updated = await r.json();
        setConfig(updated); setConfigDraft(updated);
        toast({ title: 'Config saved' });
      }
    } finally { setBusy(false); }
  };

  const saveBootcampConfig = async () => {
    setBusy(true);
    try {
      // Coerce numeric fields
      const body = { ...bootcampDraft };
      ['total_spots', 'earlybird_spots_remaining', 'spots_remaining'].forEach((k) => {
        if (body[k] !== '' && body[k] !== null && body[k] !== undefined) {
          body[k] = parseInt(body[k], 10);
        }
      });
      const r = await fetch(`${BACKEND}/api/admin/bootcamp/config`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (r.ok) {
        const updated = await r.json();
        setBootcampCfg(updated); setBootcampDraft(updated);
        toast({ title: 'Bootcamp config saved' });
      } else {
        const e = await r.json().catch(() => ({}));
        toast({ title: 'Save failed', description: e.detail || 'Unknown error', variant: 'destructive' });
      }
    } finally { setBusy(false); }
  };

  const copyToClipboard = async (text, label) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      toast({ title: `${label} copied` });
    } catch {
      toast({ title: 'Copy failed', variant: 'destructive' });
    }
  };

  if (loading || !stats) {
    return <div className="p-8 text-[#666]" data-testid="admin-nurture-loading">Loading…</div>;
  }

  return (
    <div className="p-6 sm:p-8 space-y-6" data-testid="admin-nurture-page">
      <header className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-[1.8rem] font-bold text-[#0B1E3F]">Mindset Nurture & Bootcamp</h1>
          <p className="text-[14px] text-[#666] mt-1">7-touch email sequence over 14 days. Worker runs every 60s.</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={fetchAll} variant="outline" disabled={busy} data-testid="admin-refresh">Refresh</Button>
          <Button onClick={triggerNow} disabled={busy} className="bg-[#0B1E3F] text-white hover:bg-[#091735]" data-testid="admin-nurture-trigger-button">
            Trigger worker now
          </Button>
        </div>
      </header>

      <Tabs defaultValue="nurture" className="w-full">
        <TabsList className="bg-[#F5F3EE] border border-black/10" data-testid="admin-tabs">
          <TabsTrigger value="nurture" data-testid="admin-tab-nurture">Nurture</TabsTrigger>
          <TabsTrigger value="pdf-tracks" data-testid="admin-tab-pdf-tracks">PDF Tracks</TabsTrigger>
          <TabsTrigger value="bootcamp" data-testid="admin-tab-bootcamp">Bootcamp</TabsTrigger>
          <TabsTrigger value="settings" data-testid="admin-tab-settings">Settings</TabsTrigger>
        </TabsList>

        {/* ============================= NURTURE TAB ============================ */}
        <TabsContent value="nurture" className="space-y-6 mt-6">
          {/* Stats grid */}
          <section className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              ['Total active', stats.total_active, 'admin-nurture-stat-active'],
              ['Completed', stats.completed, 'admin-nurture-stat-completed'],
              ['Unsubscribed', stats.unsubscribed, 'admin-nurture-stat-unsub'],
              ['Sent (24h)', stats.sent_last_24h, 'admin-nurture-stat-sent24h'],
            ].map(([label, n, tid]) => (
              <div key={label} className="rounded-xl border border-black/10 bg-white p-4" data-testid={tid}>
                <p className="text-[11px] uppercase tracking-[0.18em] text-[#666] font-semibold">{label}</p>
                <p className="mt-1">{statBadge(n)}</p>
              </div>
            ))}
          </section>

          {/* By-step distribution */}
          <section className="bg-white rounded-xl border border-black/10 p-5">
            <h2 className="font-heading text-[15px] font-bold text-[#0B1E3F] mb-3">Active contacts by step</h2>
            <div className="grid grid-cols-8 gap-2">
              {[0,1,2,3,4,5,6,7].map((n) => (
                <div key={n} className="text-center bg-[#F5F3EE] rounded-lg p-3" data-testid={`admin-nurture-step-${n}`}>
                  <p className="text-[10px] text-[#666] uppercase tracking-wider">{n === 0 ? 'New' : `After #${n}`}</p>
                  <p className="font-mono font-bold text-[#0B1E3F] mt-1">{stats.by_step[n] || 0}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Add test contact */}
          <section className="bg-white rounded-xl border border-black/10 p-5">
            <h2 className="font-heading text-[15px] font-bold text-[#0B1E3F] mb-3">Add a test contact</h2>
            <form onSubmit={addTestContact} className="flex flex-col sm:flex-row gap-2">
              <Input placeholder="First name" value={testName} onChange={(e)=>setTestName(e.target.value)} className="sm:max-w-[200px]" data-testid="admin-nurture-test-firstname" />
              <Input type="email" placeholder="Email" value={testEmail} onChange={(e)=>setTestEmail(e.target.value)} required data-testid="admin-nurture-test-email" />
              <Button type="submit" disabled={busy || !testEmail} className="bg-[#0B1E3F] text-white hover:bg-[#091735]" data-testid="admin-nurture-test-submit">
                Enroll + send Email 1 now
              </Button>
            </form>
          </section>

          {/* Recent log */}
          <section className="bg-white rounded-xl border border-black/10 p-5">
            <h2 className="font-heading text-[15px] font-bold text-[#0B1E3F] mb-3">Recent email log (last 50)</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]" data-testid="admin-nurture-log-table">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wider text-[#666] border-b border-black/10">
                    <th className="py-2 pr-3">Time</th>
                    <th className="py-2 pr-3">Contact</th>
                    <th className="py-2 pr-3">Email #</th>
                    <th className="py-2 pr-3">Subject</th>
                    <th className="py-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {log.map((row) => (
                    <tr key={row.id} className="border-b border-black/5">
                      <td className="py-2 pr-3 font-mono text-[12px] text-[#666]">{new Date(row.sent_at).toLocaleString()}</td>
                      <td className="py-2 pr-3">{row.contact_email || '—'}</td>
                      <td className="py-2 pr-3 font-mono">#{row.email_number}</td>
                      <td className="py-2 pr-3 text-[#3a3a3a]">{row.subject}</td>
                      <td className="py-2">
                        <span className={`text-[11px] font-bold uppercase tracking-wider ${
                          row.status === 'sent' ? 'text-emerald-600' : row.status === 'failed' ? 'text-red-600' : 'text-amber-600'
                        }`}>{row.status}</span>
                      </td>
                    </tr>
                  ))}
                  {log.length === 0 && (
                    <tr><td colSpan={5} className="py-6 text-center text-[#666]">No emails sent yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </TabsContent>

        {/* ============================= PDF TRACKS TAB ============================ */}
        <TabsContent value="pdf-tracks" className="space-y-6 mt-6" data-testid="admin-pdf-tracks-tab">
          {/* Per-track stats cards */}
          {byTrack && (
            <section className="space-y-3">
              <h2 className="font-heading text-[15px] font-bold text-[#0B1E3F]">
                Per-track funnel metrics
              </h2>
              <p className="text-[12px] text-[#666] -mt-1">
                Each PDF lead-magnet has its own 3-email sequence (Day 0 → 3 → 7).
                The legacy 7-touch Mindset Quiz bucket is shown for comparison.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
                {byTrack.tracks.map((t) => {
                  const isActive = selectedTrack === t.track;
                  return (
                    <button
                      type="button"
                      key={t.track}
                      onClick={() => setSelectedTrack(t.track)}
                      className={`text-left rounded-xl border bg-white p-4 transition-shadow focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#D4A434] ${
                        isActive ? 'border-[#0B1E3F] shadow-md ring-1 ring-[#0B1E3F]/30' : 'border-black/10 hover:shadow-sm'
                      }`}
                      data-testid={`admin-pdf-track-card-${t.track}`}
                    >
                      <div className="flex items-start justify-between gap-2 mb-3">
                        <div>
                          <p className="text-[10px] uppercase tracking-[0.18em] text-[#666] font-semibold">
                            {t.track.replace(/_/g, ' ')}
                          </p>
                          <p className="text-[13px] font-bold text-[#0B1E3F] mt-1 leading-tight">
                            {t.label}
                          </p>
                        </div>
                        <span className="inline-block h-3 w-3 rounded-full mt-1 flex-shrink-0" style={{ backgroundColor: t.color }} aria-hidden="true" />
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-[12px]">
                        <div>
                          <p className="text-[10px] text-[#666] uppercase tracking-wider">Active</p>
                          <p className="font-mono font-bold text-[#0B1E3F]">{t.active}</p>
                        </div>
                        <div>
                          <p className="text-[10px] text-[#666] uppercase tracking-wider">Completed</p>
                          <p className="font-mono font-bold text-[#0B1E3F]">{t.completed}</p>
                        </div>
                        <div>
                          <p className="text-[10px] text-[#666] uppercase tracking-wider">Sent (24h)</p>
                          <p className="font-mono font-bold text-emerald-600">{t.sent_24h}</p>
                        </div>
                        <div>
                          <p className="text-[10px] text-[#666] uppercase tracking-wider">Sent total</p>
                          <p className="font-mono font-bold text-[#0B1E3F]">{t.sent_total}</p>
                        </div>
                        <div>
                          <p className="text-[10px] text-[#666] uppercase tracking-wider">Failed</p>
                          <p className={`font-mono font-bold ${t.failed_total > 0 ? 'text-red-600' : 'text-[#666]'}`}>
                            {t.failed_total}
                          </p>
                        </div>
                        <div>
                          <p className="text-[10px] text-[#666] uppercase tracking-wider">Unsub</p>
                          <p className="font-mono font-bold text-[#666]">{t.unsubscribed}</p>
                        </div>
                      </div>
                      {/* Per-email funnel sparkline (sent counts at each step in the sequence) */}
                      <div className="mt-3 pt-3 border-t border-black/5">
                        <p className="text-[10px] text-[#666] uppercase tracking-wider mb-1">
                          Email funnel (lifetime sends)
                        </p>
                        <div className="flex gap-1 items-end h-8">
                          {Object.entries(t.funnel).map(([n, count]) => {
                            const max = Math.max(1, ...Object.values(t.funnel));
                            const pct = (count / max) * 100;
                            return (
                              <div key={n} className="flex-1 flex flex-col items-center" title={`Email #${n}: ${count} sent`}>
                                <div
                                  className="w-full rounded-t-sm"
                                  style={{
                                    height: `${Math.max(4, pct)}%`,
                                    backgroundColor: t.color,
                                    opacity: count > 0 ? 0.85 : 0.15,
                                  }}
                                />
                                <span className="text-[9px] font-mono text-[#666] mt-0.5">{count}</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </section>
          )}

          {/* Send-a-test-sequence form */}
          <section className="bg-white rounded-xl border border-black/10 p-5" data-testid="admin-pdf-test-send">
            <h2 className="font-heading text-[15px] font-bold text-[#0B1E3F] mb-1 flex items-center gap-2">
              <Send className="h-4 w-4" /> Send a test sequence
            </h2>
            <p className="text-[12px] text-[#666] mb-4 leading-relaxed">
              Fires real Resend deliveries to the target inbox so you can review the actual rendered emails.
              Subject lines are prefixed with <code className="bg-[#F5F3EE] px-1 rounded text-[11px]">[TEST]</code>.
              Does not create a contact and does not affect any stats above. Throttled to 4 req/sec.
            </p>
            <form onSubmit={sendPdfTestSequence} className="grid grid-cols-1 sm:grid-cols-[1fr_180px_220px_auto] gap-2">
              <Input
                type="email" required placeholder="target@inbox.com"
                value={pdfTestEmail} onChange={(e)=>setPdfTestEmail(e.target.value)}
                data-testid="admin-pdf-test-email"
              />
              <Input
                placeholder="First name (e.g. Coach)" value={pdfTestName}
                onChange={(e)=>setPdfTestName(e.target.value)}
                data-testid="admin-pdf-test-firstname"
              />
              <Select value={pdfTestTrack} onValueChange={setPdfTestTrack}>
                <SelectTrigger data-testid="admin-pdf-test-track-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All 3 tracks (9 emails)</SelectItem>
                  <SelectItem value="parents_pdf">Parents (3 emails)</SelectItem>
                  <SelectItem value="athletes_pdf">Athletes (3 emails)</SelectItem>
                  <SelectItem value="coaches_pdf">Coaches (3 emails)</SelectItem>
                </SelectContent>
              </Select>
              <Button
                type="submit" disabled={pdfTestBusy || !pdfTestEmail.trim()}
                className="bg-[#0B1E3F] text-white hover:bg-[#091735]"
                data-testid="admin-pdf-test-submit"
              >
                {pdfTestBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Send test'}
              </Button>
            </form>
            {pdfTestResult && (
              <div className="mt-4 text-[12px]" data-testid="admin-pdf-test-result">
                <p className="font-bold text-[#0B1E3F] mb-1">
                  Result: {pdfTestResult.sent_count} sent · {pdfTestResult.failed_count} failed → {pdfTestResult.target}
                </p>
                <ul className="space-y-1">
                  {pdfTestResult.sent.map((s, i) => (
                    <li key={`s${i}`} className="text-emerald-700 font-mono text-[11px]">
                      ✓ [{s.track} E{s.email_number}] {s.subject}
                    </li>
                  ))}
                  {pdfTestResult.failed.map((s, i) => (
                    <li key={`f${i}`} className="text-red-600 font-mono text-[11px]">
                      ✗ [{s.track} E{s.email_number}] {s.error}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </section>

          {/* Per-track recent log */}
          <section className="bg-white rounded-xl border border-black/10 p-5">
            <header className="flex items-center justify-between flex-wrap gap-2 mb-3">
              <h2 className="font-heading text-[15px] font-bold text-[#0B1E3F] flex items-center gap-2">
                <Activity className="h-4 w-4" /> Recent log
                {byTrack && (
                  <span className="text-[12px] font-normal text-[#666]">
                    · {byTrack.tracks.find((t) => t.track === selectedTrack)?.label}
                  </span>
                )}
              </h2>
              <Button
                variant="outline" size="sm"
                onClick={() => fetchTrackLog(selectedTrack)}
                disabled={trackLogLoading}
                data-testid="admin-pdf-track-log-refresh"
              >
                {trackLogLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Refresh'}
              </Button>
            </header>
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]" data-testid="admin-pdf-track-log-table">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wider text-[#666] border-b border-black/10">
                    <th className="py-2 pr-3">Time</th>
                    <th className="py-2 pr-3">Contact</th>
                    <th className="py-2 pr-3">Email #</th>
                    <th className="py-2 pr-3">Subject</th>
                    <th className="py-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {trackLog.map((row) => (
                    <tr key={row.id} className="border-b border-black/5">
                      <td className="py-2 pr-3 font-mono text-[12px] text-[#666]">
                        {new Date(row.sent_at).toLocaleString()}
                      </td>
                      <td className="py-2 pr-3">{row.contact_email || '—'}</td>
                      <td className="py-2 pr-3 font-mono">#{row.email_number}</td>
                      <td className="py-2 pr-3 text-[#3a3a3a]">{row.subject}</td>
                      <td className="py-2">
                        <span className={`text-[11px] font-bold uppercase tracking-wider ${
                          row.status === 'sent' ? 'text-emerald-600'
                            : row.status === 'failed' ? 'text-red-600'
                            : 'text-amber-600'
                        }`}>{row.status}</span>
                      </td>
                    </tr>
                  ))}
                  {!trackLogLoading && trackLog.length === 0 && (
                    <tr>
                      <td colSpan={5} className="py-6 text-center text-[#666]">
                        No emails on this track yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </TabsContent>

        {/* ============================= BOOTCAMP TAB ============================ */}
        <TabsContent value="bootcamp" className="space-y-6 mt-6">
          {bootcampSignups && (
            <section className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="admin-bootcamp-stats">
              {[
                ['Total signups', bootcampSignups.summary.total_signups, 'admin-bc-total'],
                ['Early-bird', bootcampSignups.summary.earlybird_signups, 'admin-bc-eb'],
                ['Standard', bootcampSignups.summary.standard_signups, 'admin-bc-std'],
                ['Revenue (USD)', `$${bootcampSignups.summary.revenue_dollars.toFixed(2)}`, 'admin-bc-rev'],
              ].map(([label, n, tid]) => (
                <div key={label} className="rounded-xl border border-black/10 bg-white p-4" data-testid={tid}>
                  <p className="text-[11px] uppercase tracking-[0.18em] text-[#666] font-semibold">{label}</p>
                  <p className="mt-1">{statBadge(n)}</p>
                </div>
              ))}
            </section>
          )}

          {bootcampDraft && (
            <section className="bg-white rounded-xl border border-black/10 p-5" data-testid="admin-bootcamp-config">
              <h2 className="font-heading text-[15px] font-bold text-[#0B1E3F] mb-1">Cohort + pricing config</h2>
              <p className="text-[12px] text-[#666] mb-4">
                These values feed the public <code>/bootcamp</code> page. Changes are live immediately.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {/* Stripe link rows with copy buttons */}
                {['stripe_earlybird_link', 'stripe_standard_link'].map((k) => (
                  <label key={k} className="text-[12px] text-[#666] font-semibold uppercase tracking-wider md:col-span-2">
                    {k.replace(/_/g,' ')}
                    <div className="flex gap-2 mt-1">
                      <Input
                        className="normal-case font-mono text-[12px]"
                        value={bootcampDraft[k] || ''}
                        onChange={(e)=>setBootcampDraft({ ...bootcampDraft, [k]: e.target.value })}
                        placeholder="https://buy.stripe.com/..."
                        data-testid={`admin-bc-input-${k}`}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => copyToClipboard(bootcampDraft[k], 'Link')}
                        disabled={!bootcampDraft[k]}
                        data-testid={`admin-bc-copy-${k}`}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </label>
                ))}
                {/* Plain text/number fields */}
                {[
                  ['earlybird_price', 'Early-bird price (USD)'],
                  ['standard_price', 'Standard price (USD)'],
                  ['cohort_start_date', 'Cohort start date'],
                  ['cohort_duration', 'Cohort duration'],
                  ['total_spots', 'Total spots'],
                  ['earlybird_spots_remaining', 'Early-bird spots remaining'],
                  ['spots_remaining', 'Total spots remaining'],
                ].map(([k, label]) => (
                  <label key={k} className="text-[12px] text-[#666] font-semibold uppercase tracking-wider">
                    {label}
                    <Input
                      className="mt-1 normal-case"
                      type={k.includes('spots') ? 'number' : 'text'}
                      value={bootcampDraft[k] ?? ''}
                      onChange={(e)=>setBootcampDraft({ ...bootcampDraft, [k]: e.target.value })}
                      data-testid={`admin-bc-input-${k}`}
                    />
                  </label>
                ))}
              </div>
              <div className="mt-5 flex gap-2 flex-wrap">
                <Button
                  onClick={saveBootcampConfig}
                  disabled={busy || JSON.stringify(bootcampDraft) === JSON.stringify(bootcampCfg)}
                  className="bg-[#0B1E3F] text-white hover:bg-[#091735]"
                  data-testid="admin-bc-save"
                >
                  Save bootcamp config
                </Button>
                <Button onClick={()=>setBootcampDraft(bootcampCfg)} variant="outline" disabled={busy} data-testid="admin-bc-reset">
                  Reset
                </Button>
              </div>
              <p className="text-[11px] text-[#666] mt-4 leading-relaxed">
                <strong>Tip:</strong> Create the two Stripe Payment Links in Stripe Dashboard → Payment Links with metadata{' '}
                <code>product=bootcamp</code> + <code>tier=earlybird</code> or <code>tier=standard</code>, success URL{' '}
                <code>https://apexathleticmind.com/bootcamp/thank-you?session_id=&#123;CHECKOUT_SESSION_ID&#125;</code>,
                then paste them above. Counters decrement automatically on each verified webhook delivery.
              </p>
            </section>
          )}

          {/* Signups table */}
          <section className="bg-white rounded-xl border border-black/10 p-5">
            <h2 className="font-heading text-[15px] font-bold text-[#0B1E3F] mb-3">Recent bootcamp signups</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]" data-testid="admin-bc-signups-table">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wider text-[#666] border-b border-black/10">
                    <th className="py-2 pr-3">When</th>
                    <th className="py-2 pr-3">Email</th>
                    <th className="py-2 pr-3">Name</th>
                    <th className="py-2 pr-3">Tier</th>
                    <th className="py-2 pr-3">Amount</th>
                    <th className="py-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {bootcampSignups?.rows?.map((row) => (
                    <tr key={row.stripe_session_id} className="border-b border-black/5">
                      <td className="py-2 pr-3 font-mono text-[12px] text-[#666]">
                        {row.created_at ? new Date(row.created_at).toLocaleString() : '—'}
                      </td>
                      <td className="py-2 pr-3">{row.email}</td>
                      <td className="py-2 pr-3 text-[#3a3a3a]">{row.full_name || row.first_name || '—'}</td>
                      <td className="py-2 pr-3">
                        <span className={`text-[11px] font-bold uppercase tracking-wider ${
                          row.tier === 'earlybird' ? 'text-[#D4A434]' : 'text-[#0B1E3F]'
                        }`}>{row.tier}</span>
                      </td>
                      <td className="py-2 pr-3 font-mono">
                        ${((row.amount_total || 0) / 100).toFixed(2)}
                      </td>
                      <td className="py-2 text-emerald-600 text-[11px] font-bold uppercase tracking-wider">{row.status}</td>
                    </tr>
                  ))}
                  {!bootcampSignups?.rows?.length && (
                    <tr><td colSpan={6} className="py-6 text-center text-[#666]">No bootcamp signups yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </TabsContent>

        {/* ============================= SETTINGS TAB ============================ */}
        <TabsContent value="settings" className="space-y-6 mt-6">
          {configDraft && (
            <section className="bg-white rounded-xl border border-black/10 p-5">
              <h2 className="font-heading text-[15px] font-bold text-[#0B1E3F] mb-1">Nurture config</h2>
              <p className="text-[12px] text-[#666] mb-4">
                Variables substituted into all 7 nurture emails (Calendly link, sender details, etc.).
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {['calendly_link','bootcamp_link','sender_email','sender_name','internal_alert_email'].map((k) => (
                  <label key={k} className="text-[12px] text-[#666] font-semibold uppercase tracking-wider">
                    {k.replace(/_/g,' ')}
                    <Input
                      className="mt-1 normal-case"
                      value={configDraft[k] || ''}
                      onChange={(e)=>setConfigDraft({ ...configDraft, [k]: e.target.value })}
                      data-testid={`admin-nurture-config-${k}`}
                    />
                  </label>
                ))}
              </div>
              <div className="mt-4 flex gap-2">
                <Button onClick={saveConfig} disabled={busy || JSON.stringify(configDraft) === JSON.stringify(config)} className="bg-[#0B1E3F] text-white hover:bg-[#091735]" data-testid="admin-nurture-config-save">
                  Save config
                </Button>
                <Button onClick={()=>setConfigDraft(config)} variant="outline" disabled={busy}>Reset</Button>
              </div>
            </section>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
